// App-wide state: settings, the active calibration, a shared frame source
// (kept open between setup and practice so the camera isn't re-requested),
// hash routing and toasts.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { speaker } from '../engine/audio';
import { db, loadSettings, saveSettings, DEFAULT_SETTINGS, type CalibrationRecord, type Settings } from '../engine/db';
import { CameraSource, DemoSource, type FrameSource } from '../engine/source';
import { LiveSimulator } from '../sim/live';
import type { Stance } from '../core/mapping';

// --- Routing -------------------------------------------------------------------

export interface Route {
  name: string;
  id?: string;
  query: URLSearchParams;
}

function parseHash(): Route {
  const raw = location.hash.replace(/^#\/?/, '');
  const [path, qs] = raw.split('?');
  const [name, id] = path.split('/');
  return { name: name || 'home', id, query: new URLSearchParams(qs ?? '') };
}

export function navigate(to: string, opts: { replace?: boolean } = {}) {
  const hash = '#/' + to.replace(/^\/+/, '');
  if (opts.replace) history.replaceState(null, '', hash);
  else history.pushState(null, '', hash);
  window.dispatchEvent(new HashChangeEvent('hashchange'));
}

export function useRoute(): Route {
  const [route, setRoute] = useState(parseHash);
  useEffect(() => {
    const on = () => setRoute(parseHash());
    window.addEventListener('hashchange', on);
    window.addEventListener('popstate', on);
    return () => {
      window.removeEventListener('hashchange', on);
      window.removeEventListener('popstate', on);
    };
  }, []);
  return route;
}

// --- Shared frame source ---------------------------------------------------------

let current: { key: string; source: FrameSource } | null = null;
let pending: { key: string; promise: Promise<FrameSource> } | null = null;
let generation = 0;

/**
 * Opens (or reuses) the camera or the demo scene. Concurrent calls share one
 * opening; a source that is superseded or fails to finish setting up is
 * closed rather than leaked (a second getUserMedia on iOS mutes the first).
 */
export async function acquireSource(kind: 'camera' | 'demo', settings: Settings, stance: Stance = 'regular'): Promise<FrameSource> {
  const key = kind === 'demo' ? `demo/${stance}` : `camera/${settings.cameraId ?? 'auto'}/${settings.resolution}/${settings.fps}`;
  if (current?.key === key && current.source.alive()) return current.source;
  if (pending?.key === key) return pending.promise;
  releaseSource();
  const gen = ++generation;
  const promise = (async () => {
    const source = kind === 'demo' ? new DemoSource(new LiveSimulator({ stance })) : await CameraSource.open(settings);
    try {
      if (source instanceof CameraSource) await source.useModel(settings.model, settings.delegate);
    } catch (e) {
      source.close();
      throw e;
    }
    if (gen !== generation) {
      source.close();
      throw new Error('Camera request was superseded.');
    }
    current = { key, source };
    return source;
  })();
  pending = { key, promise };
  try {
    return await promise;
  } finally {
    if (pending?.promise === promise) pending = null;
  }
}

/** Ensures the camera source uses the current model settings. */
export async function refreshModel(source: FrameSource, settings: Settings) {
  if (source instanceof CameraSource) await source.useModel(settings.model, settings.delegate);
}

export function releaseSource() {
  generation++;
  current?.source.close();
  current = null;
  pending = null;
}

// --- Context ----------------------------------------------------------------------

interface AppState {
  ready: boolean;
  settings: Settings;
  updateSettings: (patch: Partial<Settings>) => void;
  calibrations: CalibrationRecord[];
  active: CalibrationRecord | null;
  reloadCalibrations: () => Promise<void>;
  toast: (msg: string) => void;
}

const Ctx = createContext<AppState | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [calibrations, setCalibrations] = useState<CalibrationRecord[]>([]);
  const [ready, setReady] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reloadCalibrations = useCallback(async () => {
    setCalibrations((await db.listCalibrations()).sort((a, b) => b.createdAt - a.createdAt));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const s = await loadSettings();
        setSettings(s);
        await reloadCalibrations();
      } catch (e) {
        console.error(e);
      }
      setReady(true);
    })();
  }, [reloadCalibrations]);

  useEffect(() => {
    speaker.voiceOn = settings.voice;
    speaker.beepsOn = settings.beeps;
    speaker.rate = settings.speechRate;
    speaker.voiceURI = settings.voiceURI;
  }, [settings]);

  const updateSettings = useCallback((patch: Partial<Settings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      void saveSettings(next);
      return next;
    });
  }, []);

  const toast = useCallback((msg: string) => {
    setToastMsg(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(null), 2600);
  }, []);

  const active = useMemo(
    () => calibrations.find((c) => c.id === settings.activeCalibrationId) ?? null,
    [calibrations, settings.activeCalibrationId],
  );

  const value = useMemo(
    () => ({ ready, settings, updateSettings, calibrations, active, reloadCalibrations, toast }),
    [ready, settings, updateSettings, calibrations, active, reloadCalibrations, toast],
  );
  return (
    <Ctx.Provider value={value}>
      {children}
      {toastMsg && (
        <div className="toast" role="status">
          {toastMsg}
        </div>
      )}
    </Ctx.Provider>
  );
}

export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp outside AppProvider');
  return v;
}

/** Object URL for a stored blob, revoked on change/unmount. */
export function useBlobUrl(key: string | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let revoked = false;
    let made: string | null = null;
    setUrl(null);
    if (key) {
      void db.getBlob(key).then((b) => {
        if (revoked || !b) return;
        made = URL.createObjectURL(b);
        setUrl(made);
      });
    }
    return () => {
      revoked = true;
      if (made) URL.revokeObjectURL(made);
    };
  }, [key]);
  return url;
}
